import applicant from './applicant-en'
import archive from './archive-en'
import calendar from './calendar-en'
import dashboard from './dashboard-en'
import drawing from './drawing-en'
import exportStrings from './export-en'
import findings from './findings-en'
import history from './history-en'
import importStrings from './import-en'
import intakeStrings from './intake-en'
import km from './km-en'
import oscStrings from './osc-en'
import pages from './pages-en'
import procedure from './procedure-en'
import queueStrings from './queue-en'
import registerExportStrings from './register-export-en'
import registerPublic from './register-public-en'
import registrations from './registrations-en'
import settings from './settings-en'
import trustStrings from './trust-en'
import workflowStrings from './workflow-en'

/**
 * Console strings for the Planning (Kebenaran Merancang) module. BM-first: the
 * statutory list statuses of spec section 7.2 are authored in ms.ts and pinned by
 * planning-console-keys.spec.ts. Keys built from ids replace dots with underscores
 * (transition `check.retry` -> `actions.check_retry`). `home` is plan A's.
 */
export default {
  home: {
    title: 'Planning permission',
    subtitle: 'Plan submissions, checks and approvals will appear here.',
  },
  settings,
  findings,
  form: { ...pages.form, ...km.form },
  report: pages.report,
  detail: pages.detail,
  dashboard: dashboard.dashboard,
  activity: dashboard.activity,
  registrations,
  mySubmissions: applicant.mySubmissions,
  downloads: applicant.downloads,
  history: history.history,
  chain: history.chain,
  calendar: calendar.calendar,
  clocks: { ...calendar.clocks, names: km.clocks },
  procedure: procedure.procedure,
  roles: procedure.roles,
  export: exportStrings.export,
  authority: exportStrings.authority,
  sealKey: exportStrings.sealKey,
  archive: archive.archive,
  imports: importStrings.imports,
  queue: queueStrings.queue,
  trustedSystems: trustStrings.trustedSystems,
  externalIds: trustStrings.externalIds,
  osc: oscStrings.osc,
  workflow: workflowStrings.workflow,
  intake: intakeStrings.intake,
  inputs: km.inputs,
  flags: km.flags,
  counters: km.counters,
  drawingStandard: drawing.drawingStandard,
  findingHelp: drawing.findingHelp,
  nextStep: drawing.nextStep,
  checkWording: drawing.checkWording,
  registerExport: registerExportStrings.registerExport,
  copyFailed: 'Copy failed: select the text and copy it.',
  list: {
    subtitle: 'Plan submissions, their drawing checks and approvals.',
    newSubmission: 'New submission',
    searchLabel: 'Search submissions',
    searchPlaceholder: 'Reference, title, applicant or file name',
    search: 'Search',
    tabsLabel: 'Filter submissions',
    tabs: {
      all: 'All',
      awaiting: 'Awaiting review',
      approved: 'Approved',
      returned: 'Returned',
      failed: 'Failed',
      withdrawn: 'Withdrawn',
      committee: 'At committee',
      refused: 'Refused',
      in_force: 'In force',
    },
    columns: {
      title: 'Title',
      applicant: 'Applicant',
      file: 'File',
      size: 'Size',
      conversion: 'Drawing check',
      approval: 'Approval',
      updated: 'Updated',
      actions: 'Actions',
    },
    view: 'View',
    empty: 'No submissions yet.',
    emptyFiltered: 'No submissions match this filter.',
    pageOf: 'Page {{page}} of {{pages}}',
    previous: 'Previous',
    next: 'Next',
    total: 'Submissions: {{total}}',
  },
  status: {
    none: '—',
    conversion: { in_progress: 'In progress', done: 'Done', failed: 'Failed' },
    approval: {
      awaiting: 'Awaiting approval',
      approved: 'Approved',
      returned: 'Returned',
      withdrawn: 'Withdrawn',
      refused: 'Refused',
      lapsed: 'Lapsed',
    },
  },
  stage: {
    draft: 'Draft',
    checking: 'Checking',
    check_failed: 'Check failed',
    awaiting_review: 'Awaiting review',
    approved: 'Approved',
    returned: 'Returned',
    withdrawn: 'Withdrawn',
    deleted: 'Deleted',
    ...km.stage,
  },
  submissionStatus: {
    queued: 'Queued',
    converting: 'Converting',
    ingesting: 'Ingesting',
    passed: 'Passed',
    passed_with_warnings: 'Passed with warnings',
    failed: 'Failed',
    error: 'Error',
  },
  severity: { fail: 'Failures', warn: 'Warnings', info: 'Notes' },
  result: { passed: 'Check passed', passed_with_warnings: 'Check passed with warnings', failed: 'Check failed' },
  actions: {
    submit: 'Submit',
    check_retry: 'Retry check',
    resubmit: 'Resubmit',
    approve: 'Approve',
    return: 'Return',
    withdraw: 'Withdraw',
    delete: 'Delete',
    check_passed: 'Check passed',
    check_failed: 'Check failed',
    check_errored: 'Check could not run',
    ...km.actions,
  },
  actionConfirm: {
    approve: 'Approve this submission? Its site and plots move to the approved map.',
    return: 'Return this submission to the applicant. They will see your reason.',
    withdraw: 'Withdraw this submission? This cannot be undone.',
    delete: 'Delete this submission? It disappears from every list; the record is kept.',
    resubmit: 'Resubmit this submission for checking. Choose which drawing is checked.',
    resubmitSame: 'Resubmit with the same drawing',
    resubmitSameHint: 'The latest drawing is checked again as the next revision.',
    resubmitReplace: 'Upload a replacement drawing',
    resubmitReplaceHint: 'The new drawing becomes the next revision.',
    resubmitNoPrevious: 'There is no earlier drawing to reuse, so upload one.',
    resubmitStoredHint: 'Your replacement drawing is already uploaded, so it is the one checked.',
    resubmitUploading: 'Uploading the drawing… {{percent}}%',
    resubmitFinalizing: 'Checking the upload…',
    resubmitSubmitting: 'Resubmitting…',
    reasonLabel: 'Reason',
    reasonRequired: 'Enter a reason.',
    confirm: 'Confirm',
    cancel: 'Cancel',
  },
  guards: {
    form_valid: 'the form is incomplete',
    documents_cad_present_or_reuse: 'no drawing has been uploaded for this revision',
    submission_current: 'the check result is out of date',
    submission_latest_errored: 'the latest check did not error',
    check_latest_passed: 'the latest check has not passed',
    register_not_dead: 'the map register has a failed update',
    separation_of_duties: 'the approver must not be the last submitter',
    input_reason_present: 'a reason is required',
    clock_running: 'the period for this step has ended or is not running',
    clock_due: 'the period has not ended yet',
    ...workflowStrings.guards,
  },
  events: {
    case_created: 'Created',
    case_attributes_updated: 'Details updated',
    document_added: 'Drawing uploaded',
    transition_submit: 'Submitted',
    transition_check_passed: 'Check passed',
    transition_check_failed: 'Check failed',
    transition_check_errored: 'Check could not run',
    transition_check_retry: 'Check retried',
    transition_resubmit: 'Resubmitted',
    transition_approve: 'Approved',
    transition_return: 'Returned',
    transition_withdraw: 'Withdrawn',
    transition_delete: 'Deleted',
    case_bound: 'Bound to its procedure',
    document_reused: 'Drawing reused',
    clock_started: 'Deadline started',
    clock_paused: 'Deadline paused',
    clock_resumed: 'Deadline resumed',
    clock_stopped: 'Deadline stopped',
    clock_extended: 'Deadline extended',
    clock_expired: 'Deadline passed',
    clock_scheduled: 'Due date set',
    ...exportStrings.events,
    ...importStrings.events,
    ...workflowStrings.events,
    ...intakeStrings.events,
  },
  attributes: {
    keterangan: 'Description',
    gtn1: 'Land use (GTN1)',
    gtn2: 'Land use (GTN2)',
    gtn3: 'Land use (GTN3)',
    nama: 'Development name',
    pemajuan: 'Development',
    pemaju: 'Developer',
    no_fail: 'File number',
    luas_hektar: 'Area (hectares)',
    negeri: 'State',
    daerah: 'District',
    mukim: 'Mukim',
    no_lot: 'Lot number',
    tahun_data: 'Data year',
    jenis_km: 'Planning permission type',
    kaedah: 'OSC filing method (Kaedah)',
    upi: 'Unique parcel identifier (UPI)',
  },
  map: {
    empty: 'No geometry to preview yet.',
    legendClasses: 'Land use',
    legendStatus: 'Status',
    statusApproved: 'Approved',
    statusStaging: 'Staging',
    statusCandidate: 'Not staged (diagnosis only)',
    unclassified: 'Unclassified',
  },
  timeline: {
    system: 'System',
    empty: 'No events yet.',
    reason: 'Reason: {{reason}}',
    stageChange: '{{from}} → {{to}}',
  },
  register: {
    ...registerPublic,
    pending: 'Map update pending.',
    dead: 'Map update failed.',
    lastError: 'Last error: {{error}}',
    retry: 'Retry map update',
    retried: 'The map update was queued again.',
  },
  errors: {
    stale_case: 'This submission changed since you opened it. It has been reloaded; try again.',
    stage_locked: 'This submission cannot be edited at its current stage.',
    guard_failed: 'Not possible yet: {{guards}}.',
    actor_not_permitted: 'You are not permitted to do that.',
    not_planning_actor:
      'You do not have a Planning role. Ask an administrator to add you to a reviewer or approver group.',
    request_id_reused: 'That request was already used for a different action. Reload and try again.',
    upload_rejected: 'The drawing was rejected ({{reason}}).',
    register_server_not_configured: 'No map register server is configured.',
    register_server_locked: 'The map register server cannot change once submissions exist.',
    invalid_settings: 'Some settings are not valid. Fix the items below and save again.',
    invalid_register_server: 'The selected map register server is not valid.',
    registration_closed: 'Applicant registration is closed.',
    rate_limited: 'Too many requests. Wait a few minutes and try again.',
    registration_not_found: 'That registration was not found.',
    registration_not_pending: 'That registration was already decided.',
    identity_exists: 'An account with this email already exists.',
    email_in_use: 'This email is used by an account in another organisation.',
    seats_exhausted: 'No seats are left for this account type.',
    seats_busy: 'Another account is being created. Try again in a moment.',
    invalid_registration: 'Some registration details are not valid.',
    document_not_found: 'That file is not available for this revision.',
    invalid_calendar: 'That calendar is not valid. Fix the items listed below and try again.',
    invalid_overlay: 'These adjustments are not valid. Fix the items listed below and save again.',
    settings_required: 'Save the Planning settings first, then adjust the procedure.',
    procedure_variant_unavailable:
      'The procedure adjustments in use cannot be loaded. An administrator must save them again or switch back to the published procedure in Settings → Planning.',
    no_decision_tables: 'This procedure has no rule tables to download.',
    openSettings: 'Open Planning settings',
    not_found: 'Not found, or you cannot see it.',
    network: 'Could not reach the server. Check your connection and try again.',
    upload_aborted: 'The upload was cancelled.',
    upload_transport: 'The upload failed. Try again.',
    generic: 'Something went wrong. Try again.',
    retryLoad: 'Retry',
    ...exportStrings.errors,
    ...importStrings.errors,
    ...queueStrings.errors,
    ...workflowStrings.errors,
    ...intakeStrings.errors,
    ...registerExportStrings.errors,
  },
} as const
